import React, { useMemo } from "react";

import {
  Box,
  FormControl,
  InputBase,
  InputBaseProps,
  InputLabel,
  Stack,
  Typography,
  alpha,
  styled,
} from "@mui/material";

import { InformationCircleIcon } from "../Icons/InformationCircle";
import { StyledTooltip } from "../tooltip/Tooltip";

const IgfdInputBase = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(2.5),
  },
  "& .MuiInputBase-input": {
    borderRadius: 8,
    position: "relative",
    border: "1px solid",
    backgroundColor: theme.palette.grey[50],
    borderColor: theme.palette.grey.A200,
    fontFamily: theme.typography.body2.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    padding: "12px 16px",
    /* Highlight with secondary color when in focus */
    "&:focus": {
      boxShadow: `${alpha(theme.palette.secondary.main, 0.1)} 0 0 0 0.2rem`,
      borderColor: theme.palette.secondary.main,
    },
    /* Used when transitioning into focus mode */
    transition: theme.transitions.create([
      "border-color",
      "background-color",
      "box-shadow",
    ]),
  },
}));

const IgfdMiniInputBase = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(2.5),
  },
  "& .MuiInputBase-input": {
    borderRadius: 8,
    position: "relative",
    border: "1px solid",
    backgroundColor: theme.palette.grey[50],
    borderColor: theme.palette.grey.A200,
    fontFamily: theme.typography.body2.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    padding: "8px 16px",
    /* Highlight with secondary color when in focus */
    "&:focus": {
      boxShadow: `${alpha(theme.palette.secondary.main, 0.1)} 0 0 0 0.2rem`,
      borderColor: theme.palette.secondary.main,
    },
    /* Used when transitioning into focus mode */
    transition: theme.transitions.create([
      "border-color",
      "background-color",
      "box-shadow",
    ]),
  },
}));

export interface IgfdInputProps extends InputBaseProps {
  label?: string | React.ReactNode;
  optional?: boolean;
  unit?: string;
  infoLabel?: string;
  mini?: boolean;
}

export const Input = ({
  label,
  optional,
  unit,
  infoLabel,
  mini,
  ...props
}: IgfdInputProps) => {
  return (
    <Box sx={{ width: "100%" }}>
      <FormControl variant="standard" fullWidth={props.fullWidth}>
        {label && (
          <IgfdInputLabel
            label={label}
            optional={optional}
            unit={unit}
            infoLabel={infoLabel}
          />
        )}
        {mini ? <IgfdMiniInputBase {...props} /> : <IgfdInputBase {...props} />}
      </FormControl>
    </Box>
  );
};

export const IgfdInputLabel = ({
  label,
  unit,
  tag,
  optional,
  infoLabel,
}: {
  label: string | React.ReactNode;
  optional?: boolean;
  unit?: string;
  tag?: string;
  infoLabel?: string;
}) => (
  <InputLabel
    shrink
    color={"primary"}
    sx={{
      // MUI's InputLabel defaults to overflow: hidden + text-overflow: ellipsis
      // when shrink is applied, which clips labels like "Description (Optional)"
      // at narrower drawer widths. Disable the truncation so the full label
      // always renders.
      maxWidth: "none",
      whiteSpace: "nowrap",
      overflow: "visible",
      textOverflow: "clip",
    }}
  >
    <InputLabelRow>
      {label}
      {tag && <Typography>{tag}</Typography>}
      <OptionalUnitComponent optional={optional} unit={unit} />
      {infoLabel && (
        <StyledTooltip infoIcon title={infoLabel} placement={"right"}>
          <Box
            component={InformationCircleIcon}
            sx={{ color: "secondary.main", flexShrink: 0 }}
          />
        </StyledTooltip>
      )}
    </InputLabelRow>
  </InputLabel>
);

const OptionalUnitComponent = ({
  unit,
  optional,
}: {
  unit?: string;
  optional?: boolean;
}) => {
  const outputText = useMemo(() => {
    return unit && optional
      ? `${unit} optional`
      : unit && !optional
      ? `${unit}`
      : "Optional";
  }, [unit, optional]);

  return (
    <>
      {(unit || optional) && (
        <Typography
          variant={"body1"}
          sx={(theme) => ({
            color: theme.palette.grey.A700,
            // The light text color needs a thicker font
            fontFamily: theme.typography.caption.fontFamily,
            fontStyle: "italic",
          })}
        >
          ({outputText})
        </Typography>
      )}
    </>
  );
};

const InputLabelRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(1),
}));
